/**
 * SetupView.jsx - Tracking > Setup: what has to be in place before the
 * numbers mean anything, as a checklist that is also the way in:
 *   1 Budget          the won quote's worksheet + change orders (BudgetTab);
 *                     "Apply to the Quote tab" stays here (cost area)
 *   2 PO lines        the PO's line items and which invoice goes to which
 *                     line (PoLinesSection)
 *   3 People & rates  sell / cost rates and per-day expenses (PeopleRatesSection)
 * Each step says done / needs a look / to do (trackingInsights.checklistOf).
 */
import { GREEN, MUTED } from '../quotes/quoteUi';
import { checklistOf } from './trackingInsights';
import BudgetTab from './BudgetTab';
import PoLinesSection from './PoLinesSection';
import PeopleRatesSection from './PeopleRatesSection';

const MARK = {
  done: { sign: '✓', bg: '#E8F5E9', fg: GREEN },
  warn: { sign: '!', bg: '#FFF3E0', fg: '#B45309' },
  todo: { sign: '', bg: '#F3F4F6', fg: '#6B7280' },
};

export default function SetupView({ step, setStep, ...props }) {
  const list = checklistOf(props.data, props.seesCosts);
  const current = list.some((s) => s.id === step) ? step : 'budget';
  return (
    <div>
      <ol
        aria-label="Setup checklist"
        style={{
          listStyle: 'none',
          margin: '0 0 16px',
          padding: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 10,
        }}
      >
        {list.map((s, i) => {
          const m = MARK[s.state];
          const on = s.id === current;
          return (
            <li key={s.id}>
              <button
                type="button"
                aria-current={on ? 'step' : undefined}
                onClick={() => setStep(s.id)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  padding: '10px 12px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  border: `1.5px solid ${on ? GREEN : '#E5E9DD'}`,
                  background: on ? '#F7FBF4' : '#fff',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    flex: '0 0 26px',
                    height: 26,
                    borderRadius: 13,
                    background: m.bg,
                    color: m.fg,
                    fontWeight: 900,
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {m.sign || i + 1}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 800, color: '#1F2328' }}>
                    {i + 1}. {s.label}
                  </span>
                  <span style={{ display: 'block', fontSize: 11, color: s.state === 'warn' ? '#B45309' : MUTED, marginTop: 2 }}>
                    <span className="sr-only">{s.state === 'done' ? 'Done: ' : s.state === 'warn' ? 'Needs a look: ' : 'To do: '}</span>
                    {s.note}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
      {current === 'budget' && <BudgetTab {...props} />}
      {current === 'po' && <PoLinesSection {...props} />}
      {current === 'people' && <PeopleRatesSection {...props} />}
    </div>
  );
}
